
import { useState, useEffect, useRef } from 'react';
import { ChatAttachIcon, SendIcon, EmojiIcon } from '../../../components/common/IconComponents';
import { messagesData, emojiOptions } from '../../../utils/Data';
import AttachmentOptions from './AttachmentOptions';
import axios from 'axios';

const ChatPage = () => {
    const [messages, setMessages] = useState(messagesData);
    const [message, setMessage] = useState('');
    const [showEmojiDropdown, setShowEmojiDropdown] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [fileSizeError, setFileSizeError] = useState(false);
    const [selectedFileType, setSelectedFileType] = useState('');
    const [attachmentOptionsVisible, setAttachmentOptionsVisible] = useState(false);
    const [previewFiles, setPreviewFiles] = useState([]); // Added state for file preview URLs

    const emojiDropdownRef = useRef(null);
    const attachmentOptionsRef = useRef(null);

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (message.trim() !== '' || attachedFiles.length > 0) {
            const newMessage = { text: message, sender: 'right', files: attachedFiles };
            setMessages((prevMessages) => [...prevMessages, newMessage]);
            setMessage('');
            setAttachedFiles([]);
            setPreviewFiles([]); // Clear the previewFiles state
            setFileSizeError(false);
        }
    };

    const handleEmojiClick = (emoji) => {
        setMessage((prevMessage) => prevMessage + emoji);
    };

    const handleOutsideClick = (e) => {
        if (emojiDropdownRef.current && !emojiDropdownRef.current.contains(e.target)) {
            setShowEmojiDropdown(false);
        }
        if (attachmentOptionsRef.current && !attachmentOptionsRef.current.contains(e.target)) {
            setAttachmentOptionsVisible(false);
        }
    };

    const handleFileIconClick = () => {
        setAttachmentOptionsVisible((prevState) => !prevState);
        setSelectedFileType('');
    };

    const handleFileTypeClick = (fileType) => {
        setSelectedFileType(fileType);
    };

    const handleFileInputChange = async (e) => {
        const files = Array.from(e.target.files);

        try {
            const uploadedFiles = await Promise.all(
                files.map(async (file) => {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('upload_preset', 'eqprvodt');

                    const response = await axios.post(
                        'https://api.cloudinary.com/v1_1/darnus3nl/image/upload',
                        formData
                    );
                    return {
                        name: file.name,
                        size: file.size,
                        url: response.data.secure_url,
                    };
                })
            );

            setAttachedFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);

            // Generate preview URLs for the selected files
            const previewURLs = uploadedFiles.map((file) => file.url);
            setPreviewFiles((prevPreviewFiles) => [...prevPreviewFiles, ...previewURLs]);
        } catch (error) {
            console.error('Error uploading file(s) to Cloudinary: ', error);
        }

        // Clear file input value to allow selecting the same file again
        e.target.value = '';
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    return (
        <div className="relative">
            <div className="px-3 py-2">
                {messages.map((message, index) => (
                    <div
                        key={index}
                        className={`flex mb-3 ${message.sender === 'right' ? 'justify-end' : 'justify-start'
                            }`}
                    >
                        <div
                            className={`p-1 rounded-lg w-fit max-w-[85%] xl:max-w-[55%] text-base font-normal ${message.sender === 'right'
                                    ? 'bg-enabled-btn text-white ml-auto'
                                    : 'bg-chat-gray-bg text-success-email mr-auto'
                                }`}
                        >
                            <p className="px-2">{message.text}</p>
                            {message.files && message.files.length > 0 && (
                                <div>
                                    {message.files.map((file, index) => (
                                        <div key={index}>
                                            <p>Attached File: {file.name}</p>
                                            <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <form className="bg-th-bg px-3 py-4 fixed bottom-0 right-0 z-10 w-full lg:w-[392px] xl:w-5/12">
                <div className="flex items-center">
                    <div
                        className="inline-flex justify-center rounded-lg cursor-pointer"
                        onClick={handleFileIconClick}
                    >
                        <ChatAttachIcon className="pr-3" />
                    </div>

                    {selectedFileType === '' && (
                        <AttachmentOptions
                            attachmentOptionsVisible={attachmentOptionsVisible}
                            handleFileTypeClick={handleFileTypeClick}
                            handleButtonClick={handleFileIconClick}
                        />
                    )}

                    {selectedFileType !== '' && (
                        <input
                            id="fileInput"
                            type="file"
                            multiple
                            accept={
                                selectedFileType === 'document'
                                    ? 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                    : selectedFileType === 'camera'
                                        ? 'image/*'
                                        : selectedFileType === 'gallery'
                                            ? 'image/*'
                                            : selectedFileType === 'audio'
                                                ? 'audio/*'
                                                : selectedFileType === 'location'
                                                    ? 'application/octet-stream'
                                                    : selectedFileType === 'contact'
                                                        ? 'text/vcard'
                                                        : ''
                            }
                            onChange={handleFileInputChange}
                            className="hidden"
                        />
                    )}

                    <div className="flex items-center ml-2">
                        <div
                            className="inline-flex justify-center rounded-lg cursor-pointer"
                            onClick={() => setShowEmojiDropdown((prevState) => !prevState)}
                        >
                            <EmojiIcon className="pr-3" />
                        </div>
                        <div
                            className={`absolute z-10 bottom-16 left-0 md:left-3 bg-th-bg px-2 py-4 md:p-4 rounded-md shadow-lg mt-2 w-full md:w-[560px] ${showEmojiDropdown ? 'block' : 'hidden'
                                }`}
                            ref={emojiDropdownRef}
                        >
                            {emojiOptions.map((emoji, index) => (
                                <span
                                    key={index}
                                    className="cursor-pointer mx-1"
                                    onClick={() => handleEmojiClick(emoji)}
                                >
                                    {emoji}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="flex-grow pl-3">
                        <input
                            type="text"
                            className="bg-white border-2 border-th-bg w-full rounded-lg px-3 py-2 text-base"
                            placeholder="Type a message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />

                        {previewFiles.length > 0 && ( // Display the file previews if there are any
                            <div>
                                {previewFiles.map((previewFile, index) => (
                                    <img
                                        key={index}
                                        src={previewFile}
                                        alt="Preview"
                                        className="w-8 h-8 object-cover rounded-lg"
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="inline-flex justify-center rounded-lg cursor-pointer pl-2"
                        onClick={handleSendMessage}
                    >
                        <SendIcon className="w-5 h-5 text-th-fg" />
                    </button>
                </div>
                {fileSizeError && (
                    <p className="mt-2 text-xs text-red-500">File size should be less than 10MB.</p>
                )}
            </form>
        </div>
    );
};

export default ChatPage;



// cloudinary not attaching
// import React, { useState, useEffect, useRef } from 'react';
// import axios from 'axios';
// import { ChatAttachIcon, SendIcon, EmojiIcon } from '../../../components/common/IconComponents';
// import { messagesData, emojiOptions } from '../../../utils/Data';
// import AttachmentOptions from './AttachmentOptions';

// const ChatPage = () => {
//     const [messages, setMessages] = useState(messagesData);
//     const [message, setMessage] = useState('');
//     const [showEmojiDropdown, setShowEmojiDropdown] = useState(false);
//     const [attachedFiles, setAttachedFiles] = useState([]);
//     const [fileSizeError, setFileSizeError] = useState(false);
//     const [selectedFileType, setSelectedFileType] = useState('');
//     const [attachmentOptionsVisible, setAttachmentOptionsVisible] = useState(false);

//     const emojiDropdownRef = useRef(null);
//     const attachmentOptionsRef = useRef(null);

//     const handleSendMessage = (e) => {
//         e.preventDefault();
//         if (message.trim() !== '' || attachedFiles.length > 0) {
//             const newMessage = { text: message, sender: 'right', files: attachedFiles };
//             setMessages((prevMessages) => [...prevMessages, newMessage]);
//             setMessage('');
//             setAttachedFiles([]);
//             setFileSizeError(false);
//         }
//     };

//     const handleEmojiClick = (emoji) => {
//         setMessage((prevMessage) => prevMessage + emoji);
//     };

//     const handleOutsideClick = (e) => {
//         if (emojiDropdownRef.current && !emojiDropdownRef.current.contains(e.target)) {
//             setShowEmojiDropdown(false);
//         }
//         if (attachmentOptionsRef.current && !attachmentOptionsRef.current.contains(e.target)) {
//             setAttachmentOptionsVisible(false);
//         }
//     };

//     const handleFileIconClick = () => {
//         setAttachmentOptionsVisible((prevState) => !prevState);
//         setSelectedFileType('');
//     };

//     const handleFileTypeClick = (fileType) => {
//         setSelectedFileType(fileType);
//     };

//     const handleFileInputChange = async (e) => {
//         const files = Array.from(e.target.files);

//         try {
//             const uploadedFiles = await Promise.all(
//                 files.map(async (file) => {
//                     const formData = new FormData();
//                     formData.append('file', file);
//                     formData.append('upload_preset', 'eqprvodt');

//                     const response = await axios.post(
//                         'https://api.cloudinary.com/v1_1/darnus3nl/image/upload',
//                         formData
//                     );
//                     return {
//                         name: file.name,
//                         size: file.size,
//                         url: response.data.secure_url,
//                     };
//                 })
//             );

//             const fileNames = uploadedFiles.map((file) => file.name).join(', ');
//             setMessage((prevMessage) => prevMessage + ' ' + fileNames);
//             setAttachedFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);
//         } catch (error) {
//             console.error('Error uploading file(s) to Cloudinary: ', error);
//         }

//         e.target.value = ''; // Clear file input value to allow selecting the same file again
//     };

//     useEffect(() => {
//         document.addEventListener('mousedown', handleOutsideClick);
//         return () => {
//             document.removeEventListener('mousedown', handleOutsideClick);
//         };
//     }, []);

//     return (
//         <div className="relative">
//             <div className="px-3 py-2">
//                 {messages.map((message, index) => (
//                     <div
//                         key={index}
//                         className={`flex mb-3 ${message.sender === 'right' ? 'justify-end' : 'justify-start'
//                             }`}
//                     >
//                         <div
//                             className={`p-1 rounded-lg w-fit max-w-[85%] xl:max-w-[55%] text-base font-normal ${message.sender === 'right'
//                                     ? 'bg-enabled-btn text-white ml-auto'
//                                     : 'bg-chat-gray-bg text-success-email mr-auto'
//                                 }`}
//                         >
//                             <p className="px-2">{message.text}</p>
//                             {message.files && message.files.length > 0 && (
//                                 <div>
//                                     {message.files.map((file, index) => (
//                                         <div key={index}>
//                                             <p>Attached File: {file.name}</p>
//                                             <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}
//                         </div>
//                     </div>
//                 ))}
//             </div>

//             <form className="bg-th-bg px-3 py-4 fixed bottom-0 right-0 z-10 w-full lg:w-[392px] xl:w-5/12">
//                 <div className="flex items-center">
//                     <div
//                         className="inline-flex justify-center rounded-lg cursor-pointer"
//                         onClick={handleFileIconClick}
//                     >
//                         <ChatAttachIcon className="pr-3" />
//                     </div>

//                     {selectedFileType === '' && (
//                         <AttachmentOptions
//                             attachmentOptionsVisible={attachmentOptionsVisible}
//                             handleFileTypeClick={handleFileTypeClick}
//                             handleButtonClick={handleFileIconClick}
//                         />
//                     )}

//                     {selectedFileType !== '' && (
//                         <input
//                             id="fileInput"
//                             type="file"
//                             multiple
//                             accept={
//                                 selectedFileType === 'document'
//                                     ? 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
//                                     : selectedFileType === 'camera'
//                                         ? 'image/*'
//                                         : selectedFileType === 'gallery'
//                                             ? 'image/*'
//                                             : selectedFileType === 'audio'
//                                                 ? 'audio/*'
//                                                 : selectedFileType === 'location'
//                                                     ? 'application/octet-stream'
//                                                     : selectedFileType === 'contact'
//                                                         ? 'text/vcard'
//                                                         : ''
//                             }
//                             onChange={handleFileInputChange}
//                             className="hidden"
//                         />
//                     )}

//                     <div className="flex items-center ml-2">
//                         <div
//                             className="inline-flex justify-center rounded-lg cursor-pointer"
//                             onClick={() => setShowEmojiDropdown((prevState) => !prevState)}
//                         >
//                             <EmojiIcon className="pr-3" />
//                         </div>
//                         <div
//                             className={`absolute z-10 bottom-16 left-0 md:left-3 bg-th-bg px-2 py-4 md:p-4 rounded-md shadow-lg mt-2 w-full md:w-[560px] ${showEmojiDropdown ? 'block' : 'hidden'
//                                 }`}
//                             ref={emojiDropdownRef}
//                         >
//                             {emojiOptions.map((emoji, index) => (
//                                 <span
//                                     key={index}
//                                     className="cursor-pointer mx-1"
//                                     onClick={() => handleEmojiClick(emoji)}
//                                 >
//                                     {emoji}
//                                 </span>
//                             ))}
//                         </div>
//                     </div>

//                     <div className="flex-grow pl-3">
//                         <input
//                             type="text"
//                             className="bg-white border-2 border-th-bg w-full rounded-lg px-3 py-2 text-base"
//                             placeholder="Type a message"
//                             value={message}
//                             onChange={(e) => setMessage(e.target.value)}
//                         />
//                     </div>

//                     <button
//                         type="submit"
//                         className="inline-flex justify-center rounded-lg cursor-pointer pl-2"
//                         onClick={handleSendMessage}
//                     >
//                         <SendIcon className="w-5 h-5 text-th-fg" />
//                     </button>
//                 </div>
//                 {fileSizeError && (
//                     <p className="mt-2 text-xs text-red-500">File size should be less than 10MB.</p>
//                 )}
//             </form>
//         </div>
//     );
// };

// export default ChatPage;



// logic with cloudinary
// import { useState, useEffect, useRef } from 'react';
// import { ChatAttachIcon, SendIcon, EmojiIcon } from '../../../components/common/IconComponents';
// import { messagesData, emojiOptions } from '../../../utils/Data';
// import AttachmentOptions from './AttachmentOptions';
// import axios from 'axios';

// const ChatPage = () => {
//     const [messages, setMessages] = useState(messagesData);
//     const [message, setMessage] = useState('');
//     const [showEmojiDropdown, setShowEmojiDropdown] = useState(false);
//     const [attachedFiles, setAttachedFiles] = useState([]);
//     const [fileSizeError, setFileSizeError] = useState(false);
//     const [selectedFileType, setSelectedFileType] = useState('');
//     const [attachmentOptionsVisible, setAttachmentOptionsVisible] = useState(false);

//     const emojiDropdownRef = useRef(null);
//     const attachmentOptionsRef = useRef(null);

//     const handleSendMessage = (e) => {
//         e.preventDefault();
//         if (message.trim() !== '' || attachedFiles.length > 0) {
//             const newMessage = { text: message, sender: 'right', files: attachedFiles };
//             setMessages((prevMessages) => [...prevMessages, newMessage]);
//             setMessage('');
//             setAttachedFiles([]);
//             setFileSizeError(false);
//         }
//     };

//     const handleEmojiClick = (emoji) => {
//         setMessage((prevMessage) => prevMessage + emoji);
//     };

//     const handleOutsideClick = (e) => {
//         if (emojiDropdownRef.current && !emojiDropdownRef.current.contains(e.target)) {
//             setShowEmojiDropdown(false);
//         }
//         if (attachmentOptionsRef.current && !attachmentOptionsRef.current.contains(e.target)) {
//             setAttachmentOptionsVisible(false);
//         }
//     };

//     const handleFileIconClick = () => {
//         setAttachmentOptionsVisible((prevState) => !prevState);
//         setSelectedFileType('');
//     };

//     const handleFileTypeClick = (fileType) => {
//         setSelectedFileType(fileType);
//     };

//     const handleFileInputChange = async (e) => {
//         const files = Array.from(e.target.files);

//         try {
//             const uploadedFiles = await Promise.all(
//                 files.map(async (file) => {
//                     const formData = new FormData();
//                     formData.append('file', file);
//                     formData.append('upload_preset', 'eqprvodt');

//                     const response = await axios.post('https://api.cloudinary.com/v1_1/darnus3nl/image/upload', formData);
//                     return {
//                         name: file.name,
//                         size: file.size,
//                         url: response.data.secure_url,
//                     };
//                 })
//             );

//             setAttachedFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);
//         } catch (error) {
//             console.error('Error uploading file(s) to Cloudinary: ', error);
//         }

//         // Clear file input value to allow selecting the same file again
//         e.target.value = '';
//     };

//     useEffect(() => {
//         document.addEventListener('mousedown', handleOutsideClick);
//         return () => {
//             document.removeEventListener('mousedown', handleOutsideClick);
//         };
//     }, []);

//     return (
//         <div className="relative">
//             <div className="px-3 py-2">
//                 {messages.map((message, index) => (
//                     <div
//                         key={index}
//                         className={`flex mb-3 ${message.sender === 'right' ? 'justify-end' : 'justify-start'}`}
//                     >
//                         <div
//                             className={`p-1 rounded-lg w-fit max-w-[85%] xl:max-w-[55%] text-base font-normal ${message.sender === 'right' ? 'bg-enabled-btn text-white ml-auto' : 'bg-chat-gray-bg text-success-email mr-auto'
//                                 }`}
//                         >
//                             <p className="px-2">{message.text}</p>
//                             {message.files && message.files.length > 0 && (
//                                 <div>
//                                     {message.files.map((file, index) => (
//                                         <div key={index}>
//                                             <p>Attached File: {file.name}</p>
//                                             <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}
//                         </div>
//                     </div>
//                 ))}
//             </div>

//             <form className="bg-th-bg px-3 py-4 fixed bottom-0 right-0 z-10 w-full lg:w-[392px] xl:w-5/12">
//                 <div className="flex items-center">
//                     <div className="inline-flex justify-center rounded-lg cursor-pointer" onClick={handleFileIconClick}>
//                         <ChatAttachIcon className="pr-3" />
//                     </div>

//                     {selectedFileType === '' && (
//                         <AttachmentOptions
//                             attachmentOptionsVisible={attachmentOptionsVisible}
//                             handleFileTypeClick={handleFileTypeClick}
//                             handleButtonClick={handleFileIconClick}
//                         />

//                     )}

//                     {selectedFileType !== '' && (
//                         // <div>
//                         <input
//                             id="fileInput"
//                             type="file"
//                             multiple
//                             accept={
//                                 selectedFileType === 'document'
//                                     ? 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
//                                     : selectedFileType === 'camera'
//                                         ? 'image/*'
//                                         : selectedFileType === 'gallery'
//                                             ? 'image/*'
//                                             : selectedFileType === 'audio'
//                                                 ? 'audio/*'
//                                                 : selectedFileType === 'location'
//                                                     ? 'application/octet-stream'
//                                                     : selectedFileType === 'contact'
//                                                         ? 'text/vcard'
//                                                         : ''
//                             }
//                             onChange={handleFileInputChange}
//                             className="hidden"
//                         />
//                         // </div>
//                     )}

//                     <div className="flex items-center ml-2">
//                         <div
//                             className="inline-flex justify-center rounded-lg cursor-pointer"
//                             onClick={() => setShowEmojiDropdown((prevState) => !prevState)}
//                         >
//                             <EmojiIcon className="pr-3" />
//                         </div>
//                         <div
//                             className={`absolute z-10 bottom-16 left-0 md:left-3 bg-th-bg px-2 py-4 md:p-4 rounded-md shadow-lg mt-2 w-full md:w-[560px] ${showEmojiDropdown ? 'block' : 'hidden'
//                                 }`}
//                             ref={emojiDropdownRef}
//                         >
//                             {emojiOptions.map((emoji, index) => (
//                                 <span
//                                     key={index}
//                                     className="cursor-pointer mx-1"
//                                     onClick={() => handleEmojiClick(emoji)}
//                                 >
//                                     {emoji}
//                                 </span>
//                             ))}
//                         </div>

//                     </div>

//                     <div className="flex-grow pl-3">
//                         <input
//                             type="text"
//                             className="bg-white border-2 border-th-bg w-full rounded-lg px-3 py-2 text-base"
//                             placeholder="Type a message"
//                             value={message}
//                             onChange={(e) => setMessage(e.target.value)}
//                         />
//                     </div>

//                     <button
//                         type="submit"
//                         className="inline-flex justify-center rounded-lg cursor-pointer pl-2"
//                         onClick={handleSendMessage}
//                     >
//                         <SendIcon className="w-5 h-5 text-th-fg" />
//                     </button>
//                 </div>
//                 {fileSizeError && <p className="mt-2 text-xs text-red-500">File size should be less than 10MB.</p>}
//             </form>
//         </div>
//     );
// };

// export default ChatPage;



// okay - fall back
// import { useState, useEffect, useRef } from 'react';
// import { ChatAttachIcon, SendIcon, EmojiIcon } from '../../../components/common/IconComponents';
// import { messagesData, emojiOptions } from '../../../utils/Data';
// import AttachmentOptions from './AttachmentOptions';

// const ChatPage = () => {
//     const [messages, setMessages] = useState(messagesData);
//     const [message, setMessage] = useState('');
//     const [showEmojiDropdown, setShowEmojiDropdown] = useState(false);
//     const [attachedFiles, setAttachedFiles] = useState([]);
//     const [fileSizeError, setFileSizeError] = useState(false);
//     const [selectedFileType, setSelectedFileType] = useState('');
//     const [attachmentOptionsVisible, setAttachmentOptionsVisible] = useState(false);

//     const emojiDropdownRef = useRef(null);
//     const attachmentOptionsRef = useRef(null);

//     // const handleSendMessage = (e) => {
//     //     e.preventDefault();
//     //     if (message.trim() !== '') {
//     //         const newMessage = { text: message, sender: 'right', files: attachedFiles };
//     //         setMessages((prevMessages) => [...prevMessages, newMessage]);
//     //         setMessage('');
//     //         setAttachedFiles([]);
//     //         setFileSizeError(false);
//     //     }
//     // };
//     const handleSendMessage = (e) => {
//         e.preventDefault();
//         if (message.trim() !== '' || attachedFiles.length > 0) { // Check if message or attachedFiles is not empty
//             const newMessage = { text: message, sender: 'right', files: attachedFiles };
//             setMessages((prevMessages) => [...prevMessages, newMessage]);
//             setMessage('');
//             setAttachedFiles([]);
//             setFileSizeError(false);
//         }
//     };

//     const handleEmojiClick = (emoji) => {
//         setMessage((prevMessage) => prevMessage + emoji);
//     };

//     const handleOutsideClick = (e) => {
//         if (emojiDropdownRef.current && !emojiDropdownRef.current.contains(e.target)) {
//             setShowEmojiDropdown(false);
//         }
//         if (attachmentOptionsRef.current && !attachmentOptionsRef.current.contains(e.target)) {
//             setAttachmentOptionsVisible(false);
//         }
//     };

//     // const handleFileIconClick = () => {
//     //     setAttachmentOptionsVisible((prevState) => !prevState);
//     // };
//     // const handleFileIconClick = () => {
//     //     setAttachmentOptionsVisible((prevState) => !prevState);
//     //     setSelectedFileType('');
//     // };
//     const handleFileIconClick = () => {
//         console.log("File icon clicked");
//         setAttachmentOptionsVisible((prevState) => !prevState);
//     };

//     const handleFileTypeClick = (fileType) => {
//         setSelectedFileType(fileType);
//     };

//     const handleFileInputChange = (e) => {
//         const files = Array.from(e.target.files);
//         let validFiles = [];
//         let invalidFiles = [];

//         files.forEach((file) => {
//             if (file.size <= 10 * 1024 * 1024) {
//                 validFiles.push(file);
//             } else {
//                 invalidFiles.push(file);
//             }
//         });

//         setAttachedFiles((prevFiles) => [...prevFiles, ...validFiles]);
//         setFileSizeError(invalidFiles.length > 0);

//         // Clear file input value to allow selecting the same file again
//         e.target.value = '';
//     };

//     useEffect(() => {
//         document.addEventListener('mousedown', handleOutsideClick);
//         return () => {
//             document.removeEventListener('mousedown', handleOutsideClick);
//         };
//     }, []);

//     return (
//         <div className="relative">
//             <div className="px-3 py-2">
//                 {messages.map((message, index) => (
//                     <div
//                         key={index}
//                         className={`flex mb-3 ${message.sender === 'right' ? 'justify-end' : 'justify-start'}`}
//                     >
//                         <div
//                             className={`p-1 rounded-lg w-fit max-w-[85%] xl:max-w-[55%] text-base font-normal ${message.sender === 'right' ? 'bg-enabled-btn text-white ml-auto' : 'bg-chat-gray-bg text-success-email mr-auto'
//                                 }`}
//                         >
//                             <p className="px-2">{message.text}</p>
//                             {message.files && message.files.length > 0 && (
//                                 <div>
//                                     {message.files.map((file, index) => (
//                                         <div key={index}>
//                                             <p>Attached File: {file.name}</p>
//                                             <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}
//                         </div>
//                     </div>
//                 ))}
//             </div>

//             <form className="bg-th-bg px-3 py-4 fixed bottom-0 right-0 z-10 w-full lg:w-[392px] xl:w-5/12">
//                 <div className="flex items-center">
//                     <div className="inline-flex justify-center rounded-lg cursor-pointer" onClick={handleFileIconClick}>
//                         <ChatAttachIcon className="pr-3" />
//                     </div>

//                     {selectedFileType === '' && (
//                         <AttachmentOptions
//                             attachmentOptionsVisible={attachmentOptionsVisible}
//                             handleFileTypeClick={handleFileTypeClick}
//                             handleButtonClick={handleFileIconClick}
//                         />

//                     )}

//                     {selectedFileType !== '' && (
//                         // <div>
//                         <input
//                             id="fileInput"
//                             type="file"
//                             multiple
//                             accept={
//                                 selectedFileType === 'document'
//                                     ? 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
//                                     : selectedFileType === 'camera'
//                                         ? 'image/*'
//                                         : selectedFileType === 'gallery'
//                                             ? 'image/*'
//                                             : selectedFileType === 'audio'
//                                                 ? 'audio/*'
//                                                 : selectedFileType === 'location'
//                                                     ? 'application/octet-stream'
//                                                     : selectedFileType === 'contact'
//                                                         ? 'text/vcard'
//                                                         : ''
//                             }
//                             onChange={handleFileInputChange}
//                             className="hidden"
//                         />
//                         // </div>
//                     )}

//                     <div className="flex items-center ml-2">
//                         <div
//                             className="inline-flex justify-center rounded-lg cursor-pointer"
//                             onClick={() => setShowEmojiDropdown((prevState) => !prevState)}
//                         >
//                             <EmojiIcon className="pr-3" />
//                         </div>
//                         <div
//                             className={`absolute z-10 bottom-16 left-0 md:left-3 bg-th-bg px-2 py-4 md:p-4 rounded-md shadow-lg mt-2 w-full md:w-[560px] ${showEmojiDropdown ? 'block' : 'hidden'
//                                 }`}
//                             ref={emojiDropdownRef}
//                         >
//                             {emojiOptions.map((emoji, index) => (
//                                 <span
//                                     key={index}
//                                     className="cursor-pointer mx-1"
//                                     onClick={() => handleEmojiClick(emoji)}
//                                 >
//                                     {emoji}
//                                 </span>
//                             ))}
//                         </div>

//                     </div>
                   
//                     <div className="flex-grow pl-3">
//                         <input
//                             type="text"
//                             className="bg-white border-2 border-th-bg w-full rounded-lg px-3 py-2 text-base"
//                             placeholder="Type a message"
//                             value={message}
//                             onChange={(e) => setMessage(e.target.value)}
//                         />
//                     </div>
                   
//                     <button
//                         type="submit"
//                         className="inline-flex justify-center rounded-lg cursor-pointer pl-2"
//                         onClick={handleSendMessage}
//                     >
//                         <SendIcon className="w-5 h-5 text-th-fg" />
//                     </button>
//                 </div>
//                 {fileSizeError && <p className="mt-2 text-xs text-red-500">File size should be less than 10MB.</p>}
//             </form>
//         </div>
//     );
// };

// export default ChatPage;